/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ArrowLeft, Plus } from 'lucide-react';
import { type JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Modal } from '../../components/ui/modal.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Client } from '../../lib/api/resources.js';
import { useClient, useClients } from '../../lib/queries/clinical.js';
import { useInvoices } from '../../lib/queries/finance.js';
import { usePlans } from '../../lib/queries/itp.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { formatInr } from '../../lib/utils/money.js';
import { AdminIntakePanel } from '../intake/admin-intake-panel.js';
import { PlanForm } from '../therapist/plan-form.js';

import { AssignTherapistControl } from './assign-therapist-control.js';

const CLIENT_STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  active: 'green',
  inactive: 'slate',
  discharged: 'amber',
};
const SESSION_TONE: Readonly<Record<string, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'rose',
};
const INVOICE_TONE: Readonly<Record<string, BadgeTone>> = {
  paid: 'green',
  partial: 'amber',
  sent: 'violet',
  overdue: 'rose',
  draft: 'slate',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const joined = `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase().slice(0, 2);
  return joined === '' ? '?' : joined;
}

function Field({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{value}</p>
    </div>
  );
}

function ClientHeader({ client }: { readonly client: Client }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-lg font-bold text-white">
          {initials(client.fullName)}
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{client.fullName}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {client.primaryDiagnosis ?? '—'}
          </p>
        </div>
        <Badge
          label={t(`status.${client.status}`)}
          tone={CLIENT_STATUS_TONE[client.status] ?? 'slate'}
        />
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field
          label={t('admin.clientDetail.age')}
          value={client.age !== null ? String(client.age) : '—'}
        />
        <AssignTherapistControl client={client} />
        <Field label={t('admin.clientDetail.guardian')} value={client.guardianName ?? '—'} />
        <Field
          label={t('admin.clientDetail.contact')}
          value={client.email ?? client.phone ?? '—'}
        />
      </div>
    </div>
  );
}

function PlansSection({ clientId }: { readonly clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const plans = usePlans();
  const [open, setOpen] = useState(false);
  const mine = (plans.data ?? []).filter((p) => p.clientId === clientId);
  const openNew = useCallback(() => {
    setOpen(true);
  }, []);
  const onCreated = useCallback(() => {
    setOpen(false);
  }, []);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">
          {t('admin.clientDetail.plans')}
        </h2>
        <button
          type="button"
          onClick={openNew}
          className="focus-ring inline-flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-hover)]"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('therapist.planForm.newPlan')}
        </button>
      </div>
      {mine.length === 0 ? (
        <p className="text-sm text-slate-400">{t('admin.clientDetail.noPlans')}</p>
      ) : (
        <ul className="space-y-3">
          {mine.map((p) => (
            <li key={p.id} className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <Link
                  to={`/therapist/plans/${p.id}`}
                  className="font-medium text-slate-800 hover:text-[var(--accent)] dark:text-slate-100"
                >
                  {p.name}
                </Link>
                <span className="text-sm text-slate-500">{p.progressPct}%</span>
              </div>
              <div className="mt-2">
                <ProgressBar value={p.progressPct} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('therapist.planForm.newPlan')}
        description={t('therapist.planForm.subtitle')}
        closeLabel={t('common.cancel')}
      >
        <PlanForm onDone={onCreated} clientId={clientId} />
      </Modal>
    </section>
  );
}

function SessionsSection({ clientId }: { readonly clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const sessions = useSessions();
  const mine = (sessions.data ?? []).filter((s) => s.clientId === clientId).slice(0, 8);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
      <h2 className="mb-4 font-semibold text-slate-800 dark:text-slate-100">
        {t('admin.clientDetail.sessions')}
      </h2>
      {mine.length === 0 ? (
        <p className="text-sm text-slate-400">{t('admin.clientDetail.noSessions')}</p>
      ) : (
        <ul className="space-y-2">
          {mine.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5 text-sm dark:border-slate-800"
            >
              <span className="text-slate-600 dark:text-slate-300">
                {s.scheduledStart.slice(0, 16).replace('T', ' ')}
              </span>
              <Badge label={t(`status.${s.status}`)} tone={SESSION_TONE[s.status] ?? 'slate'} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BillingSection({ clientId }: { readonly clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const invoices = useInvoices();
  const mine = (invoices.data ?? []).filter((i) => i.clientId === clientId);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
      <h2 className="mb-4 font-semibold text-slate-800 dark:text-slate-100">
        {t('admin.clientDetail.billing')}
      </h2>
      {mine.length === 0 ? (
        <p className="text-sm text-slate-400">{t('admin.clientDetail.noInvoices')}</p>
      ) : (
        <ul className="space-y-2">
          {mine.map((i) => (
            <li
              key={i.id}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5 text-sm dark:border-slate-800"
            >
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {i.invoiceNumber}
              </span>
              <span className="text-slate-600 dark:text-slate-300">{formatInr(i.amountPaise)}</span>
              <Badge
                label={t(`status.${i.status}`, i.status)}
                tone={INVOICE_TONE[i.status] ?? 'slate'}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Admin → Client 360° profile: identity + plans + sessions + billing in one place. */
export function ClientDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const client = useClient(id);
  // Prime the cache for cross-links (therapist names etc.).
  useClients();

  return (
    <div className="space-y-6 p-8">
      <Link
        to="/admin/clients"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('admin.clientDetail.back')}
      </Link>
      <QueryState
        isLoading={client.isLoading}
        isError={client.isError}
        isEmpty={client.data === undefined}
        loadingLabel={t('common.loading')}
        errorLabel={t('admin.clientDetail.loadError')}
        emptyLabel={t('admin.clientDetail.notFound')}
      >
        {client.data !== undefined && (
          <>
            <ClientHeader client={client.data} />
            <div className="grid gap-6 lg:grid-cols-2">
              <PlansSection clientId={id} />
              <SessionsSection clientId={id} />
            </div>
            <BillingSection clientId={id} />
            <AdminIntakePanel clientId={id} />
          </>
        )}
      </QueryState>
    </div>
  );
}
